/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, Calendar, FileText, Sparkles, UserRound, ArrowRight, Tag } from 'lucide-react';
import { getFamilyDashboard, assignToFamilyMember } from '@/lib/pricing';
import { AppointmentRow, DocRow, MemberSummaryCard } from './family/familyParts';

const FamilyMembersDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeKey, setActiveKey] = useState('self');
  const [assignDialog, setAssignDialog] = useState(null); // { kind, resource_id }

  const load = useCallback(async () => {
    try {
      const d = await getFamilyDashboard();
      setData(d);
    } catch {
      setData({ has_family: false });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // For the "Assegna" dialog we prefetch the patient's own appointments that
  // haven't been tagged yet (so the owner can quickly attribute them).
  const openAssign = async (kind, resource_id) => {
    setAssignDialog({ kind, resource_id });
  };

  // Standalone assign flow: from the "self" tab, quickly send an existing appt to a member
  const handleAssign = async (member_key) => {
    if (!assignDialog) return;
    try {
      await assignToFamilyMember(assignDialog.kind, assignDialog.resource_id, member_key);
      toast.success(member_key === 'self' ? 'Riportato al titolare' : 'Assegnato al familiare');
      setAssignDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore assegnazione');
    }
  };

  const active = useMemo(() => {
    if (!data) return null;
    if (activeKey === 'self') return data.self;
    return (data.members || []).find(m => m.key === activeKey) || data.self;
  }, [data, activeKey]);

  if (loading) return <Skeleton className="h-48 rounded-3xl" data-testid="fd-loading" />;
  if (!data?.has_family) return null;

  const allMembers = [data.self, ...(data.members || [])];

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      data-testid="family-members-dashboard">
      <Card className="rounded-3xl border-primary/25 overflow-hidden">
        <CardHeader className="bg-gradient-to-br from-primary/10 via-card to-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-5 w-5 text-primary" />
                La tua famiglia
              </CardTitle>
              <CardDescription>
                Ogni familiare vede solo le sue visite e ricette. Puoi assegnare voci esistenti dal tab «Io».
              </CardDescription>
            </div>
            <Badge className="bg-primary/15 text-primary border-0 gap-1">
              <Sparkles className="h-3 w-3" /> {data.bundle_label} · −{data.discount_pct}%
            </Badge>
          </div>
          {/* Member cards row */}
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {allMembers.map((m) => (
              <MemberSummaryCard
                key={m.key}
                member={m}
                isSelf={m.key === 'self'}
                active={activeKey === m.key}
                onClick={() => setActiveKey(m.key)}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <AnimatePresence mode="wait">
            {active && (
              <motion.div
                key={active.key}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
                data-testid={`fd-panel-${active.key}`}
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm">{active.full_name}</h3>
                  {active.key !== 'self' && (
                    <Badge variant="outline" className="text-[10px]">{active.relation}</Badge>
                  )}
                </div>
                {/* Appointments */}
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Visite ({active.counts.appointments})
                  </div>
                  {active.recent_appointments.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic py-2">Nessuna visita registrata.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {active.recent_appointments.map((a) => (
                        <AppointmentRow
                          key={a.id}
                          a={a}
                          onAssign={active.key === 'self' ? openAssign : null}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {/* Documents */}
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Ricette / Referti ({active.counts.documents})
                  </div>
                  {active.recent_documents.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic py-2">Nessun documento registrato.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {active.recent_documents.map((d) => (
                        <DocRow
                          key={d.id}
                          d={d}
                          onAssign={active.key === 'self' ? openAssign : null}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {active.key !== 'self' && active.counts.appointments === 0 && (
                  <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-muted-foreground">
                    Suggerimento: apri il tab <b>«{data.self.full_name}»</b>, tocca <b>«Assegna»</b> su una visita per attribuirla a {active.full_name}.
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Assign dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="fd-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-primary" /> Assegna a familiare
            </DialogTitle>
            <DialogDescription>Scegli il familiare a cui appartiene questa voce.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            {(data?.members || []).map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => handleAssign(m.key)}
                className="w-full text-left rounded-xl border bg-card hover:bg-primary/5 p-2.5 flex items-center gap-2"
                data-testid={`fd-assign-target-${m.key}`}
              >
                <UserRound className="h-4 w-4 text-primary" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{m.full_name}</div>
                  <div className="text-[11px] text-muted-foreground">{m.relation}</div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleAssign('self')}
              className="w-full text-left rounded-xl border bg-card hover:bg-primary/5 p-2.5 flex items-center gap-2"
              data-testid="fd-assign-target-self"
            >
              <UserRound className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <div className="font-semibold text-sm">Rimuovi assegnazione</div>
                <div className="text-[11px] text-muted-foreground">Riporta al titolare del bundle</div>
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)} data-testid="fd-assign-cancel">Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default FamilyMembersDashboard;
